import CryptoJS from 'crypto-js';

/**
 * Decrypt AES encrypted text using the same logic as the backend implementation
 * This is the frontend equivalent of the Node.js/C# Decrypt method
 * @param {string} cipherText - Base64 encoded encrypted text
 * @returns {string} - Decrypted plain text or null if decryption fails
 */
export function decrypt(cipherText) {
  const EncryptionKey = "HPTV7TECHCPDS57382";
  
  try {
    // Replace spaces with + (same as backend Replace(" ", "+"))
    const cleanCipherText = cipherText.replace(/ /g, "+");
    
    // Create the salt bytes exactly as in backend
    // [0x49, 0x76, 0x61, 0x6e, 0x20, 0x4d, 0x65, 0x64, 0x76, 0x65, 0x64, 0x65, 0x76]
    // "Ivan Medvedev" in hex
    const saltHex = "4976616e204d65647665646576";
    const salt = CryptoJS.enc.Hex.parse(saltHex);
    
    // Derive key and IV using PBKDF2 (equivalent to Rfc2898DeriveBytes)
    const keyAndIv = CryptoJS.PBKDF2(EncryptionKey, salt, {
      keySize: 12, // 48 bytes = 12 * 4 = 48 bytes (32 for key + 16 for IV)
      iterations: 1000,
      hasher: CryptoJS.algo.SHA1
    });
    
    // Extract key (first 32 bytes = 8 words) and IV (next 16 bytes = 4 words)
    const key = CryptoJS.lib.WordArray.create(keyAndIv.words.slice(0, 8));
    const iv = CryptoJS.lib.WordArray.create(keyAndIv.words.slice(8, 12));
    
    // Decrypt using AES-256-CBC
    const decrypted = CryptoJS.AES.decrypt(cleanCipherText, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    
    // Try different encodings to match C# Encoding.Unicode (UTF-16LE)
    let result = null;
    
    // First try UTF-16LE
    try {
      result = decrypted.toString(CryptoJS.enc.Utf16LE);
      console.log('UTF-16LE result:', result);
      if (result && result.length > 0) {
        return result.replace(/\0/g, '').trim();
      }
    } catch (e) {
      console.log('UTF-16LE failed:', e.message);
    }
    
    // Fallback to UTF-8
    try {
      result = decrypted.toString(CryptoJS.enc.Utf8);
      console.log('UTF-8 result:', result);
      if (result && result.length > 0) {
        return result.replace(/\0/g, '').trim();
      }
    } catch (e) {
      console.log('UTF-8 failed:', e.message);
    }
    
    return null;
  } catch (error) {
    console.error('Decryption error:', error.message);
    return null;
  }
}

/**
 * Parse query parameters and decrypt credentials if present
 * @param {string} search - Search string from location (including ?)
 * @returns {Object} - Object with decrypted credentials or null
 */
export function parseEncryptedCredentials(search) {
  try {
    const params = new URLSearchParams(search);
    const encryptedUsername = params.get('Username');
    const encryptedToken = params.get('Token');
    
    if (encryptedUsername && encryptedToken) {
      // Remove single quotes if present
      const cleanUsername = encryptedUsername.replace(/'/g, '');
      const cleanToken = encryptedToken.replace(/'/g, '');
      
      console.log('🔐 Encrypted URL Parameters Found:');
      console.log('Raw Username:', encryptedUsername);
      console.log('Raw Token:', encryptedToken);
      console.log('Cleaned Username:', cleanUsername);
      console.log('Cleaned Token:', cleanToken);

      const decryptedUsername = decrypt(cleanUsername);
      const decryptedToken = decrypt(cleanToken);

      console.log('🔓 Decrypted Results:');
      console.log('Decrypted Username:', decryptedUsername);
      console.log('Decrypted Token:', decryptedToken ? '*'.repeat(decryptedToken.length) : 'Failed to decrypt');

      if (decryptedUsername && decryptedToken) {
        return {
          username: decryptedUsername,
          token: decryptedToken,
          isEncrypted: true
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error parsing encrypted credentials:', error.message);
    return null;
  }
}

// ===== DISCIPLINE DATA ENCRYPTION/DECRYPTION =====

// Secret key for discipline data encryption - different from credentials
const DISCIPLINE_SECRET_KEY = process.env.REACT_APP_DISCIPLINE_ENCRYPT_KEY || 'nabl-discipline-secure-key-2024';

/**
 * Encrypt a string value for discipline data
 * @param {string} text - Text to encrypt
 * @returns {string} - Encrypted text
 */
export const encryptText = (text) => {
    try {
        if (!text) return '';
        const encrypted = CryptoJS.AES.encrypt(String(text), DISCIPLINE_SECRET_KEY).toString();
        // URL encode the encrypted string to make it safe for query parameters
        return encodeURIComponent(encrypted);
    } catch (error) {
        console.error('Discipline encryption error:', error);
        return text; // Return original text if encryption fails
    }
};

/**
 * Decrypt a string value for discipline data
 * @param {string} encryptedText - Encrypted text to decrypt
 * @returns {string} - Decrypted text
 */
export const decryptText = (encryptedText) => {
    try {
        if (!encryptedText) return '';
        // URL decode first
        const decodedText = decodeURIComponent(encryptedText);
        const decrypted = CryptoJS.AES.decrypt(decodedText, DISCIPLINE_SECRET_KEY);
        const result = decrypted.toString(CryptoJS.enc.Utf8);
        return result || '';
    } catch (error) {
        console.error('Discipline decryption error:', error);
        return encryptedText; // Return original text if decryption fails
    }
};

/**
 * Encrypt discipline data for query parameters
 * @param {string|number} disciplineId - Discipline ID
 * @param {string} disciplineName - Discipline Name
 * @returns {object} - Object with encrypted disciplineId, disciplineName, groupId, and groupName
 */
export const encryptDisciplineData = (disciplineId, disciplineName, groupId, groupName) => {
    return {
        disciplineId: encryptText(String(disciplineId)),
        disciplineName: encryptText(String(disciplineName)),
        groupId: encryptText(String(groupId)),
        groupName: encryptText(String(groupName))
    };
};

/**
 * Decrypt discipline data from query parameters
 * @param {string} encryptedDisciplineId - Encrypted discipline ID
 * @param {string} encryptedDisciplineName - Encrypted discipline name
 * @returns {object} - Object with decrypted disciplineId, disciplineName, groupId, and groupName
 */
export const decryptDisciplineData = (encryptedDisciplineId, encryptedDisciplineName, encryptedGroupId, encryptedGroupName) => {
    return {
        disciplineId: decryptText(encryptedDisciplineId),
        disciplineName: decryptText(encryptedDisciplineName),
        groupId: decryptText(encryptedGroupId),
        groupName: decryptText(encryptedGroupName)
    };
};

/**
 * Create encrypted query string for navigation
 * @param {string|number} disciplineId - Discipline ID
 * @param {string} disciplineName - Discipline Name
 * @param {number} tab - Tab number (optional)
 * @returns {string} - Query string with encrypted parameters
 */
export const createEncryptedQueryString = (disciplineId, disciplineName, groupId, groupName, tab = null) => {
    const encrypted = encryptDisciplineData(disciplineId, disciplineName, groupId, groupName);
    const params = new URLSearchParams();
    
    params.append('disciplineId', encrypted.disciplineId);
    params.append('disciplineName', encrypted.disciplineName);
    params.append('groupId', encrypted.groupId);
    params.append('groupName', encrypted.groupName);
    
    if (tab !== null) {
        params.append('tab', String(tab));
    }
    
    return params.toString();
};

/**
 * Parse encrypted query parameters for discipline data
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {object} - Object with decrypted discipline data and tab
 */
export const parseEncryptedQuery = (searchParams) => {
    const encryptedDisciplineId = searchParams.get('disciplineId');
    const encryptedDisciplineName = searchParams.get('disciplineName');
    const encryptedGroupId = searchParams.get('groupId');
    const encryptedGroupName = searchParams.get('groupName');
    const tab = searchParams.get('tab');
    
    const decrypted = decryptDisciplineData(encryptedDisciplineId, encryptedDisciplineName,encryptedGroupId,encryptedGroupName);
    
    return {
        disciplineId: decrypted.disciplineId,
        disciplineName: decrypted.disciplineName,
        groupId: decrypted.groupId,
        groupName: decrypted.groupName,
        tab: tab ? parseInt(tab, 10) : null
    };
};

// ===== APP ID ENCRYPTION/DECRYPTION =====

/**
 * Encrypt app ID for URL parameters
 * @param {string|number} appId - Application ID
 * @returns {string} Encrypted app ID
 */
export const encryptAppId = (appId) => {
    return encryptText(appId.toString());
};

/**
 * Decrypt app ID from URL parameters
 * @param {string} encryptedAppId - Encrypted application ID
 * @returns {string} Decrypted app ID
 */
export const decryptAppId = (encryptedAppId) => {
    return decryptText(encryptedAppId);
};